import React from 'react'
import cx from 'classnames'
import * as styles from './field-text.module.css'
import * as oxfordStyles from './field-text-oxford.module.css'
import FormFieldError, {
  FormFieldErrorClassNameProps,
  FormFieldErrorProps,
} from '@cinch/design-system/src/components/forms/field-error/field-error.component'
import FormLabel, {
  FormLabelClassNameProps,
} from '@cinch/design-system/src/components/forms/field-label/field-label.component'
import { sentenceCase } from '@cinch/design-system/src/components/forms/utils'
import { ClassNamesApiProps, OxfordLegacyProps } from '@cinch/design-system/src/components/types'

export interface TextFieldClassNameProps {
  container?: string
  label?: FormLabelClassNameProps
  labelOptionalText?: string
  input?: string
  error?: FormFieldErrorClassNameProps
}

export interface TextFieldProps extends OxfordLegacyProps, ClassNamesApiProps<TextFieldClassNameProps> {
  id: string
  name: string
  type: string

  label?: string

  disabled?: boolean
  optional?: boolean
  fullWidth?: boolean

  oxdValid?: boolean
  oxdOverride?: boolean

  error?: FormFieldErrorProps

  [attr: string]: any
}

const defaultErrorProps = { show: false, collapse: false }

const TextField: React.FC<TextFieldProps> = ({
  id,
  name,
  type,
  label,
  placeholder,
  optional = false,
  disabled = false,
  fullWidth = false,
  oxford = false,
  oxdValid = false,
  oxdOverride = false,
  error = defaultErrorProps,
  classNames,
  ...rest
}) => {
  const containerClassNames = oxford
    ? cx({
        [oxfordStyles.fullWidth]: fullWidth,
      })
    : cx({
        [styles.fullWidth]: fullWidth,
      })

  const inputClassNames = oxford
    ? cx(
        oxfordStyles.fieldTextOxford,
        {
          [oxfordStyles.fullWidth]: fullWidth,
          [oxfordStyles.invalid]: error?.show,
          [oxfordStyles.valid]: oxdValid,
          [oxfordStyles.password]: rest.value && type === 'password',
          [oxfordStyles.override]: oxdOverride,
        },
        classNames?.input,
      )
    : cx(
        styles.fieldText,
        {
          [styles.fullWidth]: fullWidth,
          [styles.disabled]: disabled,
          [styles.error]: error?.show,
        },
        classNames?.input,
      )

  const labelClassNames = oxford
    ? {
        label: oxfordStyles.formLabelOxford,
        ...classNames?.label,
      }
    : classNames?.label

  return (
    <div className={cx(containerClassNames, classNames?.container)} data-testid="TextField/Container">
      {label && (
        <FormLabel htmlFor={id} classNames={labelClassNames}>
          {label}
          {!oxford && optional && (
            <span
              className={cx(styles.optionalLabel, classNames?.labelOptionalText)}
              data-testid="TextField/OptionalLabel"
            >
              &nbsp;(optional)
            </span>
          )}
        </FormLabel>
      )}

      <input
        id={id}
        name={name}
        type={type}
        disabled={disabled}
        placeholder={sentenceCase(placeholder)}
        aria-invalid={error?.show}
        className={inputClassNames}
        data-testid="TextField/Input"
        {...rest}
      />
      <FormFieldError {...error} classNames={classNames?.error} />
    </div>
  )
}

export default TextField
